import { lazy, Suspense, useEffect, useMemo, useState } from "react";

import {
  Button,
  EmptyState,
  Field,
  PageBody,
  PageHeader,
  StatusPill,
  TextArea,
  TextInput,
  cx,
} from "../../components/ui";
import {
  EMPTY_GRAPH_HELPER,
  IVR_MAX_EDGES,
  IVR_MAX_GOTO_JUMPS,
  IVR_MAX_GRAPH_BYTES,
  IVR_MAX_NODES,
  IVR_MAX_WAIT_SILENCE_S,
  SYSTEM_MAP,
  activateIvrScript,
  appendSnippet,
  catalogDeskFeatures,
  catalogPalette,
  createIvrScript,
  fallbackSnippets,
  formatScriptIssue,
  getIvrScript,
  getScriptCatalog,
  graphIsSparse,
  graphToJson,
  isIvrUnavailable,
  isSystemScript,
  ivrErrorMessage,
  listIvrScripts,
  listIvrSnippets,
  listIvrTemplates,
  parseGraphJson,
  saveIvrScript,
  validateScript,
  type IvrGraph,
  type IvrScript,
  type IvrScriptSummary,
  type IvrSnippet,
  type IvrTemplate,
  type ScriptCatalog,
  type ScriptIssue,
} from "./api";
import { useStore } from "../../lib/store";

const IvrBlockly = lazy(async () => {
  const mod = await import("./blockly/IvrBlockly");
  return { default: mod.IvrBlockly };
});

type DesignerPane = "code" | "visual" | "docs";

type SnippetVariant = "primary" | "subtle" | "success" | "amber" | "ghost";

function snippetButtonVariant(id: string): SnippetVariant {
  switch ((id || "").trim().toLowerCase()) {
    case "ident_12":
    case "ident_gate":
    case "ident":
      return "primary";
    case "hold_o":
    case "hold":
      return "amber";
    case "capture_code":
    case "capture_otp":
    case "capture":
      return "success";
    case "dtmf_menu":
    case "dtmf_12":
    case "dtmf":
      return "subtle";
    case "hangup":
    case "hangup_normal":
      return "ghost";
    case "say":
    case "say_line":
      return "success";
    default:
      return "subtle";
  }
}

function snippetButtonLabel(label: string): string {
  const text = (label || "").trim() || "step";
  return /^add\s/i.test(text) ? text : `Add ${text}`;
}

function PaneToggle({
  value,
  onChange,
}: {
  value: DesignerPane;
  onChange: (next: DesignerPane) => void;
}) {
  return (
    <div
      className="inline-flex rounded-[var(--radius-control)] p-0.5 hairline"
      role="tablist"
      aria-label="Code, Visual, or Docs"
    >
      {(["code", "visual", "docs"] as const).map((pane) => (
        <button
          key={pane}
          type="button"
          role="tab"
          aria-selected={value === pane}
          className={cx(
            "rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-sm font-medium capitalize",
            value === pane ? "bg-surface text-ink" : "text-mute hover:text-ink",
          )}
          onClick={() => onChange(pane)}
        >
          {pane === "code" ? "Code" : pane === "visual" ? "Visual" : "Docs"}
        </button>
      ))}
    </div>
  );
}

function IssueList({ issues }: { issues: ScriptIssue[] }) {
  if (!issues.length) return null;
  return (
    <ul
      className="space-y-1 rounded-[var(--radius-card)] border border-fail/40 bg-fail/10 px-4 py-3 font-mono text-[12px] text-fail"
      role="alert"
    >
      {issues.map((issue, index) => (
        <li key={`${issue.path}:${issue.line}:${issue.col}:${index}`}>{formatScriptIssue(issue)}</li>
      ))}
    </ul>
  );
}

function formatBytes(value: number): string {
  if (value >= 1024 * 1024) return `${Math.round(value / (1024 * 1024))} MiB`;
  if (value >= 1024) return `${Math.round(value / 1024)} KiB`;
  return `${value} B`;
}

function DocsPanel({ catalog }: { catalog: ScriptCatalog | null }) {
  if (!catalog) {
    return <EmptyState title="Docs" body="Tag catalog is not loaded yet." />;
  }
  const nodes = catalogPalette(catalog);
  const caps = catalog.caps || {};
  const ivrCaps = catalog.ivr.caps || {};
  const desk = catalogDeskFeatures(catalog);
  return (
    <div className="space-y-6 text-sm">
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">System map</h3>
        <p className="rounded-[var(--radius-card)] border border-accent/40 bg-accent/5 px-4 py-3 font-mono text-[12px] leading-relaxed text-ink">
          {catalog.architecture || SYSTEM_MAP}
        </p>
        <p className="mt-2 text-xs text-mute">{SYSTEM_MAP}. Operator never on audio. No originate / WebRTC.</p>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Caps</h3>
        <ul className="grid grid-cols-1 gap-2 font-mono text-[12px] text-mute sm:grid-cols-2">
          <li>nodes {caps.max_nodes ?? catalog.ivr.max_nodes ?? IVR_MAX_NODES}</li>
          <li>edges {caps.max_edges ?? catalog.ivr.max_edges ?? IVR_MAX_EDGES}</li>
          <li>
            graph {formatBytes(caps.max_graph_bytes ?? catalog.ivr.max_graph_bytes ?? IVR_MAX_GRAPH_BYTES)}
          </li>
          <li>goto {caps.max_goto_jumps ?? ivrCaps.max_goto_jumps ?? IVR_MAX_GOTO_JUMPS}</li>
          <li>silence {caps.max_wait_silence_s ?? ivrCaps.max_wait_silence_s ?? IVR_MAX_WAIT_SILENCE_S}s</li>
          <li>script {formatBytes(caps.max_script_bytes ?? 0) || "—"}</li>
          <li>spoken {caps.max_spoken_nodes ?? "—"}</li>
        </ul>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Vars</h3>
        {desk.vars.length ? (
          <div className="flex flex-wrap gap-1.5">
            {desk.vars.map((token) => (
              <span key={token} className="rounded-[var(--radius-control)] px-2 py-1 font-mono text-[11px] text-ink hairline">
                {`{{${token}}}`}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-mute">No spoken vars in this catalog yet.</p>
        )}
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Country / lang</h3>
        {desk.countryLang.length ? (
          <p className="font-mono text-[12px] text-mute">{desk.countryLang.join(" · ")}</p>
        ) : (
          <p className="text-xs text-mute">No country or lang branches in this catalog yet.</p>
        )}
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Known tags</h3>
        <div className="space-y-3">
          {catalog.tags.items.map((tag) => (
            <div key={tag.name} className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-3">
              <div className="font-mono text-[13px] text-ink">&lt;{tag.name}&gt;</div>
              {tag.attrs.length ? (
                <div className="mt-1 font-mono text-[11px] text-mute">attrs: {tag.attrs.join(", ")}</div>
              ) : null}
              {tag.flags.length ? (
                <div className="font-mono text-[11px] text-mute">flags: {tag.flags.join(", ")}</div>
              ) : null}
              {tag.note ? <p className="mt-1.5 text-xs leading-relaxed text-mute">{tag.note}</p> : null}
            </div>
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Forbidden tags</h3>
        <p className="mb-2 text-xs text-mute">Rejected at parse. Never originate / ESL / WebRTC from a script.</p>
        <div className="flex flex-wrap gap-1.5">
          {catalog.tags.forbidden.map((name) => (
            <span key={name} className="rounded-[var(--radius-control)] px-2 py-1 font-mono text-[11px] text-fail hairline">
              {name}
            </span>
          ))}
        </div>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">IVR nodes</h3>
        <p className="mb-2 text-xs text-mute">
          schema_version={catalog.ivr.schema_version} · max {catalog.ivr.max_nodes} nodes / {catalog.ivr.max_edges}{" "}
          edges · {formatBytes(catalog.ivr.max_graph_bytes)}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {nodes.map((row) => (
            <span
              key={row.type}
              title={row.disabled ? row.reason || row.note : row.note}
              className={cx(
                "rounded-[var(--radius-control)] px-2 py-1 font-mono text-[11px] hairline",
                row.disabled ? "text-mute/60 line-through" : "text-ink",
              )}
            >
              {row.type}
              {row.disabled ? " · off" : ""}
            </span>
          ))}
        </div>
        <div className="mt-3 text-xs text-mute">
          edge when: {catalog.ivr.edge_when.join(", ") || "default"}
        </div>
        <p className="mt-2 text-xs text-mute">
          dtmf kind={catalog.ivr.dtmf.kind.join("|")} charset={catalog.ivr.dtmf.charset}
          {catalog.ivr.dtmf.planned ? " (planned)" : " (live)"}. {catalog.ivr.dtmf.note}
        </p>
      </section>
      <section>
        <h3 className="mb-2 text-sm font-medium text-ink">Pack files</h3>
        <ul className="space-y-1 font-mono text-[12px] text-mute">
          {catalog.pack_files.map((name) => (
            <li key={name}>{name}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

  /**
   * Blockly Visual + JSON Code. Same schema_version=1 document both ways.
   */
  export function IvrDesigner() {
  const { toast } = useStore();
  const [scripts, setScripts] = useState<IvrScriptSummary[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [doc, setDoc] = useState<IvrScript | null>(null);
  const [graph, setGraph] = useState<IvrGraph | null>(null);
  const [draftName, setDraftName] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pane, setPane] = useState<DesignerPane>("visual");
  const [codeText, setCodeText] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<ScriptCatalog | null>(null);
  const [issues, setIssues] = useState<ScriptIssue[]>([]);
  const [templates, setTemplates] = useState<IvrTemplate[]>([]);
  const [snippets, setSnippets] = useState<IvrSnippet[]>(() => fallbackSnippets());
  const [visualEpoch, setVisualEpoch] = useState(0);

  const locked = isSystemScript(doc);
  const sparse = graphIsSparse(graph);

  const selectedSummary = useMemo(
    () => scripts.find((item) => item.slug === selectedSlug) ?? null,
    [scripts, selectedSlug],
  );

  async function refreshList(prefer?: string): Promise<IvrScriptSummary[]> {
    const rows = await listIvrScripts();
    setUnavailable(false);
    setScripts(rows);
    const next = prefer && rows.some((row) => row.slug === prefer) ? prefer : rows[0]?.slug ?? null;
    setSelectedSlug(next);
    return rows;
  }

  async function loadScript(slug: string) {
    const script = await getIvrScript(slug);
    setDoc(script);
    setGraph(script.graph);
    setDraftName(script.name);
    setCodeText(graphToJson(script.graph));
    setFormError(null);
    setVisualEpoch((n) => n + 1);
  }

  async function applyCode(text: string, pretty: boolean): Promise<boolean> {
    if (!graph) return false;
    let parsed: IvrGraph;
    try {
      parsed = parseGraphJson(text, graph.id);
      parsed.id = graph.id;
      parsed.system = Boolean(graph.system);
    } catch (err) {
      const detail = ivrErrorMessage(err, "invalid graph");
      setFormError(detail);
      setIssues([{ path: "graph", pos: 0, line: 1, col: 1, message: detail }]);
      return false;
    }
    try {
      const result = await validateScript({ kind: "ivr", graph: parsed });
      if (!result.ok) {
        setIssues(result.errors);
        setFormError(result.errors[0] ? formatScriptIssue(result.errors[0]) : "invalid graph");
        return false;
      }
    } catch (err) {
      const detail = ivrErrorMessage(err, "validate failed");
      setFormError(detail);
      setIssues([{ path: "graph", pos: 0, line: 1, col: 1, message: detail }]);
      return false;
    }
    setGraph(parsed);
    if (pretty) setCodeText(graphToJson(parsed));
    setFormError(null);
    setIssues([]);
    return true;
  }

  async function onValidateCode() {
    let graphObj: unknown;
    try {
      graphObj = JSON.parse(codeText) as unknown;
    } catch {
      const issue: ScriptIssue = { path: "graph", pos: 0, line: 1, col: 1, message: "invalid JSON" };
      setIssues([issue]);
      setFormError("invalid JSON");
      return;
    }
    try {
      const result = await validateScript({ kind: "ivr", graph: graphObj });
      setIssues(result.errors);
      if (result.ok) {
        setFormError(null);
        toast("Graph is valid", "ok");
      } else {
        setFormError(result.errors[0] ? formatScriptIssue(result.errors[0]) : "invalid graph");
      }
    } catch (err) {
      const detail = ivrErrorMessage(err, "validate failed");
      setFormError(detail);
      setIssues([{ path: "graph", pos: 0, line: 1, col: 1, message: detail }]);
    }
  }

  async function switchPane(next: DesignerPane) {
    if (next === pane) return;
    if (pane === "code" && next === "visual") {
      const ok = await applyCode(codeText, false);
      if (!ok) return;
      setVisualEpoch((n) => n + 1);
    }
    if (next === "code" && graph) {
      setCodeText(graphToJson(graph));
      setFormError(null);
      setIssues([]);
    }
    setPane(next);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listIvrScripts();
        if (cancelled) return;
        setUnavailable(false);
        setScripts(rows);
        try {
          const nextCatalog = await getScriptCatalog();
          if (!cancelled) setCatalog(nextCatalog);
        } catch {
          /* Docs tab stays empty until catalog is live */
        }
        try {
          const rowsTpl = await listIvrTemplates();
          if (!cancelled) setTemplates(rowsTpl);
        } catch {
          /* Autofill chips stay empty until templates are live */
        }
        try {
          const rowsSnip = await listIvrSnippets();
          if (!cancelled) setSnippets(rowsSnip.length ? rowsSnip : fallbackSnippets());
        } catch {
          if (!cancelled) setSnippets(fallbackSnippets());
        }
        const first = rows[0]?.slug ?? null;
        if (cancelled) return;
        setSelectedSlug(first);
        if (first) {
          try {
            await loadScript(first);
          } catch (err) {
            if (!cancelled) toast(ivrErrorMessage(err, "Could not open script"), "error");
          }
        }
      } catch (err) {
        if (cancelled) return;
        if (isIvrUnavailable(err)) {
          setUnavailable(true);
          setScripts([]);
          setDoc(null);
          setGraph(null);
        } else {
          toast(ivrErrorMessage(err, "Could not load IVR scripts"), "error");
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  async function selectScript(slug: string) {
    setSelectedSlug(slug);
    setBusy(true);
    try {
      await loadScript(slug);
    } catch (err) {
      toast(ivrErrorMessage(err, "Could not open script"), "error");
    } finally {
      setBusy(false);
    }
  }

  function patchGraph(patch: Partial<IvrGraph>) {
    setGraph((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  function applyTemplateGraph(template: IvrTemplate) {
    if (!graph || locked) {
      toast("Clone the office script to customize", "warn");
      return;
    }
    if (!template.graph.nodes.length) {
      toast("This starter has no steps", "warn");
      return;
    }
    const next: IvrGraph = {
      ...template.graph,
      id: graph.id,
      name: graph.name || template.name,
      system: false,
    };
    setGraph(next);
    setCodeText(graphToJson(next));
    setVisualEpoch((n) => n + 1);
    toast(`Filled ${template.name}`, "ok");
  }

  function applySnippet(snippet: IvrSnippet) {
    if (!graph || locked) {
      toast("Clone the office script to customize", "warn");
      return;
    }
    const next = appendSnippet(graph, snippet);
    setGraph(next);
    setCodeText(graphToJson(next));
    setVisualEpoch((n) => n + 1);
    toast(`Added ${snippet.label.replace(/^Add\s+/i, "")}`, "ok");
  }

  async function onCreateFromTemplate(templateId: string) {
    const name = draftName.trim() || templateId.replace(/_/g, " ");
    setBusy(true);
    try {
      const created = await createIvrScript(name, undefined, templateId);
      await refreshList(created.slug);
      setDoc(created);
      setGraph(created.graph);
      setDraftName(created.name);
      setCodeText(graphToJson(created.graph));
      setFormError(null);
      setVisualEpoch((n) => n + 1);
      toast("Script created from template", "ok");
    } catch (err) {
      toast(ivrErrorMessage(err, "Create refused"), "error");
    } finally {
      setBusy(false);
    }
  }

  async function onCreate() {
    const name = draftName.trim() || "script";
    setBusy(true);
    try {
      const created = await createIvrScript(name);
      await refreshList(created.slug);
      setDoc(created);
      setGraph(created.graph);
      setDraftName(created.name);
      setCodeText(graphToJson(created.graph));
      setFormError(null);
      setVisualEpoch((n) => n + 1);
      toast("Script created", "ok");
    } catch (err) {
      if (isIvrUnavailable(err)) {
        setUnavailable(true);
        toast("IVR API is not live yet", "warn");
      } else {
        toast(ivrErrorMessage(err, "Create refused"), "error");
      }
    } finally {
      setBusy(false);
    }
  }

  async function onClone() {
    if (!selectedSlug) {
      toast("Select a script first", "warn");
      return;
    }
    const name = (draftName.trim() || selectedSummary?.name || selectedSlug).replace(/\s+$/, "") + " copy";
    setBusy(true);
    try {
      const created = await createIvrScript(name, selectedSlug);
      await refreshList(created.slug);
      setDoc(created);
      setGraph(created.graph);
      setDraftName(created.name);
      setCodeText(graphToJson(created.graph));
      setFormError(null);
      setVisualEpoch((n) => n + 1);
      toast("Script cloned", "ok");
    } catch (err) {
      toast(ivrErrorMessage(err, "Clone refused"), "error");
    } finally {
      setBusy(false);
    }
  }

  async function onSave() {
    if (!graph || !selectedSlug) return;
    if (locked) {
      toast("Clone the office script to customize", "warn");
      return;
    }
    let nextGraph = graph;
    if (pane === "code") {
      try {
        const parsed = parseGraphJson(codeText, graph.id);
        parsed.id = graph.id;
        parsed.system = Boolean(graph.system);
        nextGraph = parsed;
        setGraph(parsed);
        setCodeText(graphToJson(parsed));
        setFormError(null);
      } catch (err) {
        const detail = ivrErrorMessage(err, "invalid graph");
        setFormError(detail);
        toast(detail, "error");
        return;
      }
    }
    if (nextGraph.nodes.length === 0) {
      toast("Add a node before saving", "warn");
      return;
    }
    setBusy(true);
    try {
      const saved = await saveIvrScript(selectedSlug, {
        ...nextGraph,
        name: nextGraph.name.trim() || selectedSlug,
      });
      setDoc(saved);
      setGraph(saved.graph);
      setCodeText(graphToJson(saved.graph));
      setFormError(null);
      setScripts((prev) =>
        prev.map((row) =>
          row.slug === saved.slug
            ? {
                ...row,
                name: saved.name,
                description: saved.description,
                updated_at: saved.updated_at,
              }
            : row,
        ),
      );
      toast(`Saved v${saved.version}`, "ok");
    } catch (err) {
      const detail = ivrErrorMessage(err, "Save refused");
      setFormError(detail);
      toast(detail, "error");
    } finally {
      setBusy(false);
    }
  }

  async function onActivate() {
    if (!doc || !selectedSlug) return;
    if (locked) {
      toast("Office verification is already the system fallback", "info");
      return;
    }
    setBusy(true);
    try {
      const activated = await activateIvrScript(selectedSlug, doc.version);
      setDoc(activated);
      setGraph(activated.graph);
      setCodeText(graphToJson(activated.graph));
      setFormError(null);
      setScripts((prev) =>
        prev.map((row) =>
          row.slug === activated.slug
            ? { ...row, active_version: activated.active_version, updated_at: activated.updated_at }
            : row,
        ),
      );
      toast(`Activated v${activated.active_version}`, "ok");
    } catch (err) {
      toast(ivrErrorMessage(err, "Activate refused"), "error");
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) {
    return (
      <PageBody>
        <PageHeader kicker="Speech" title="Scripts" />
        <EmptyState title="Loading" body="Fetching scripts." />
      </PageBody>
    );
  }

  if (unavailable) {
    return (
      <PageBody>
        <PageHeader kicker="Speech" title="Scripts" />
        <EmptyState
          title="Scripts API not ready"
          body="This page talks to GET /api/ivr. The endpoint is not live yet — when it lands you can design office flows here."
        />
      </PageBody>
    );
  }

  return (
    <PageBody className="flex flex-col">
      <PageHeader
        kicker="Speech"
        title="Scripts"
        actions={
          <>
            <Button variant="subtle" disabled={busy} onClick={() => void onCreate()}>
              Create
            </Button>
            <Button variant="ghost" disabled={busy || !selectedSlug} onClick={() => void onClone()}>
              Clone
            </Button>
            <Button variant="subtle" disabled={busy || !graph || locked} onClick={() => void onSave()}>
              Save
            </Button>
            <Button variant="teal" disabled={busy || !doc || locked} onClick={() => void onActivate()}>
              Activate
            </Button>
          </>
        }
      />
      <p className="mb-4 max-w-2xl text-sm text-mute">
        Code, Blockly Visual, or Docs — same schema_version=1 graph. Autofill a starter, then drag
        unlimited SAY / DTMF / hold-music steps. Office is the system default — clone it, then save and
        activate. Pick the script on New call.
      </p>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 xl:grid-cols-[minmax(16rem,20rem)_1fr]">
        <div className="space-y-4">
          <Field label="new script name">
            <TextInput
              value={draftName}
              placeholder="office copy"
              onChange={(event) => setDraftName(event.target.value)}
            />
          </Field>
          {scripts.length === 0 ? (
            <EmptyState
              title="No scripts yet"
              body="Create a flow. It starts from office verification; edit Code or Visual."
              action={
                <Button disabled={busy} onClick={() => void onCreate()}>
                  Create
                </Button>
              }
            />
          ) : (
            <div className="space-y-2">
              {scripts.map((item) => {
                const active = item.slug === selectedSlug;
                return (
                  <button
                    key={item.slug}
                    type="button"
                    onClick={() => void selectScript(item.slug)}
                    className={cx(
                      "w-full rounded-[var(--radius-card)] border px-4 py-3 text-left transition",
                      active
                        ? "border-accent bg-surface"
                        : "border-border bg-surface/60 hover:border-accent/40",
                    )}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium text-ink">{item.name}</span>
                      <div className="flex flex-wrap gap-1.5">
                        <StatusPill tone="mute">v{item.active_version}</StatusPill>
                        {item.system || item.slug === "office" ? (
                          <StatusPill tone="teal">system</StatusPill>
                        ) : null}
                      </div>
                    </div>
                    <div className="mt-1 font-mono text-[11px] text-mute">{item.slug}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-5">
          {!graph || !doc ? (
            <EmptyState
              title="Select a script"
              body="Pick a script on the left, or create one. Starts from office verification; edit Code or Visual."
            />
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                    {doc.slug}
                  </div>
                  <h2 className="text-lg font-medium text-ink">{graph.name || doc.name}</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <PaneToggle value={pane} onChange={(next) => void switchPane(next)} />
                  <StatusPill tone="mute">v{doc.version}</StatusPill>
                  {doc.version !== doc.active_version ? (
                    <StatusPill tone="live">active v{doc.active_version}</StatusPill>
                  ) : (
                    <StatusPill tone="ok">active</StatusPill>
                  )}
                  {locked ? <StatusPill tone="teal">system · office</StatusPill> : null}
                </div>
              </div>

              {locked ? (
                <p className="text-sm text-mute">
                  System office script is read-only. Clone it to customize.
                </p>
              ) : null}

              {formError ? (
                <div
                  className="rounded-[var(--radius-card)] border border-fail/40 bg-fail/10 px-4 py-3 text-sm text-fail"
                  role="alert"
                >
                  {formError}
                </div>
              ) : null}

              {pane === "docs" ? (
                <DocsPanel catalog={catalog} />
              ) : pane === "code" ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-ink">IvrGraph JSON</h3>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="ghost" disabled={busy} onClick={() => void onValidateCode()}>
                        Validate
                      </Button>
                      <Button
                        variant="subtle"
                        disabled={locked || busy}
                        onClick={() => {
                          void applyCode(codeText, true);
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                  <TextArea
                    value={codeText}
                    readOnly={locked}
                    spellCheck={false}
                    autoComplete="off"
                    rows={28}
                    className="min-h-[28rem] font-mono text-[12px] leading-relaxed"
                    aria-label="IvrGraph JSON"
                    onChange={(event) => {
                      setCodeText(event.target.value);
                      if (formError) setFormError(null);
                    }}
                    onBlur={() => {
                      if (!locked) void applyCode(codeText, false);
                    }}
                  />
                  <IssueList issues={issues} />
                  <p className="text-xs text-mute">
                    schema_version=1. Validate or Apply posts kind=ivr. Invalid JSON keeps the last good graph.
                  </p>
                </div>
              ) : (
                <>
                  {sparse ? (
                    <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-3 text-sm text-mute">
                      {EMPTY_GRAPH_HELPER}. Autofill a starter or drag START from the toolbox.
                    </div>
                  ) : null}

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field label="name">
                      <TextInput
                        value={graph.name}
                        disabled={locked}
                        onChange={(event) => patchGraph({ name: event.target.value })}
                      />
                    </Field>
                    <Field label="nodes / edges">
                      <div className="rounded-[var(--radius-control)] px-3 py-2.5 font-mono text-sm text-mute hairline">
                        {graph.nodes.length} steps · {graph.edges.length} links · start {graph.start || "—"}
                      </div>
                    </Field>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-medium text-ink">Autofill</h3>
                    <p className="mb-2 text-xs text-mute">
                      First-party starters. Fill this script, or Create as a new one. Extra template ids from
                      the server show up here. Compatible with office AMD → human → DTMF → hold O.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {templates.map((item) => (
                        <div key={item.id} className="flex gap-1">
                          <Button
                            variant="subtle"
                            className="min-h-10"
                            disabled={busy || locked}
                            title={item.description}
                            onClick={() => applyTemplateGraph(item)}
                          >
                            {item.name}
                          </Button>
                          <Button
                            variant="ghost"
                            className="min-h-10 px-2"
                            disabled={busy}
                            title={`Create a new script from ${item.name}`}
                            onClick={() => void onCreateFromTemplate(item.id)}
                          >
                            +
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="mb-2 text-sm font-medium text-ink">Add steps</h3>
                    <p className="mb-2 text-xs text-mute">
                      Large buttons append onto this script. They do not replace Autofill. System office is
                      locked — clone it first.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {snippets.map((item) => (
                        <Button
                          key={item.id}
                          variant={snippetButtonVariant(item.id)}
                          className="min-h-12 px-4 text-sm"
                          disabled={busy || locked}
                          title={item.description || item.label}
                          onClick={() => applySnippet(item)}
                        >
                          {snippetButtonLabel(item.label)}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <Suspense fallback={<EmptyState title="Loading Blockly" body="Designer is loading." />}>
                    <IvrBlockly
                      graph={graph}
                      locked={locked}
                      reloadKey={`${selectedSlug ?? ""}:${visualEpoch}`}
                      onChange={(next) => {
                        setGraph(next);
                        setCodeText(graphToJson(next));
                      }}
                    />
                  </Suspense>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </PageBody>
  );
}
